import {
    Link,
    useLoaderData,
    LoaderFunctionArgs,
    useNavigate,
    useRevalidator,
} from "react-router-dom";
import {Badge} from "../../components/Badge";
import {createApolloClient} from "../../providers/ApolloClientFactory";
import {VENDOR_CATAGORIES, VENDORS_LIST_QUERY} from "./queries";
import {organizationStore, useOrganizationStore} from "../../store/organization";
import {Vendor} from "../../types/vendor";
import {useEffect, useRef, useState, useMemo, useCallback} from "react";
import {eventStore, useEventStore} from "../../store/event";
import moment from "moment/moment";
import {usePermissions} from "../auth/api";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";
import {
    ColDef,
    GridApi,
    SizeColumnsToContentStrategy,
} from "ag-grid-community";
import {Input} from "../../components/inputs/Input";
import {PrimaryButton, SecondaryButton} from "../../components/Button";
import {AgGridReact} from "ag-grid-react";
import {usdFormat} from "../../utils";
import {userStore} from "../../store/user";
import VendorImportCSVModal from "../../components/importCsv/VendorImportCSVModal";
import Switch from "react-switch";
import {useGridEdit} from "../helpers/GridEditHelper";
import {useGridSave} from "../helpers/UseGridSave";
import TextSuggestEditor from "../helpers/TextSuggestEditorHelper";
import {UPDATE_VENDOR} from "./mutations";
import GridEditActions from "../helpers/GridEditActions";

const client = createApolloClient();
const {getState} = organizationStore;
const {getState: getEventStore} = eventStore;
const {getState: getUserStore} = userStore;

const buildSearch = (search: string) => [
    {
        name: {
            _ilike: `%${search}%`,
        },
    },
    {
        type: {
            _ilike: `%${search}%`,
        },
    },
    {
        other_type: {
            _ilike: `%${search}%`,
        },
    },
];

type CellRenderer = (params: {
    value:
        | string
        | number
        | boolean
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | React.ReactFragment
        | React.ReactPortal
        | null
        | undefined;
    data: Vendor;
}) => JSX.Element;

const createCellRenderer = (
    permission: boolean,
    linkFunc: (params: { data: Vendor }) => string
): CellRenderer => {
    return (params) => (
        <>
            {permission ? (
                <Link to={linkFunc(params)}>
                    <Badge>{params.value}</Badge>
                </Link>
            ) : (
                <Badge>{params.value}</Badge>
            )}
        </>
    );
};

export const vendorsListLoader = async ({request}: LoaderFunctionArgs) => {
    const url = new URL(request.url);
    const search = url.searchParams.get("search");
    const rawOrganizationId = getState().organizationId;
    const organizationId = parseInt(rawOrganizationId !== null ? rawOrganizationId.toString() : '', 10);
    const eventId = getEventStore().eventId;
    const user = getUserStore().user;

    let where: any = {
        _or: search ? buildSearch(search) : undefined,
    };
    if (organizationId && organizationId !== 0) {
        where = {
            ...where,
            organization_id: {
                _eq: organizationId,
            },
        };
    }
    if (eventId) {
        where = {
            ...where,
            locations: { event_id: { _eq: eventId } },
        };
    }
    if ((user?.vendors?.length || 0) > 0) {
        where = {
            ...where,
            id: {
                _in: user?.vendors,
            },
        };
    }

    const vendorTypeWhere = organizationId
        ? {
            organization_id: { _in: [0, organizationId] },
            is_active: { _eq: true },
            reference_type: { _eq: "vendor_type" },
        }
        : {};

    const [{ data: vendorsData }, { data: vendorTypeData }] = await Promise.all([
        client.query({ query: VENDORS_LIST_QUERY, variables: { where } }),
        client.query({ query: VENDOR_CATAGORIES, variables: { where: vendorTypeWhere } }),
    ]);

    return {
        vendors: vendorsData.vendors,
        vendorTypes: vendorTypeData.dashboard_reference,
    };
};

export const VendorList = () => {
    const {editCreatePermission, ListPermission, moduleWisePermission} =
        usePermissions("vendors");
    const navigate = useNavigate();
    const revalidator = useRevalidator();
    const gridRef = useRef<GridApi>();
    const originalActiveRef = useRef<Map<string, boolean>>(new Map());

    const { vendors, vendorTypes } = useLoaderData() as {
        vendors: Vendor[];
        vendorTypes: Array<{ reference_label: string; reference_value: string }>;
    };

    const headerOrgId = useOrganizationStore((s) => s.organizationId);
    const headerEventId = useEventStore((s) => s.eventId);
    const didMountRef = useRef(false);
    useEffect(() => {
        if (!didMountRef.current) {
            didMountRef.current = true;
            return;
        }
        revalidator.revalidate();
    }, [headerOrgId, headerEventId]);

    const getPatchedValue = (p: any, field?: string) => {
        if (!field) return p.value;
        const id = String(p.data?.id ?? "");
        const patch = p.context?.pending?.[id];
        if (patch && Object.prototype.hasOwnProperty.call(patch, field)) {
            return (patch as any)[field];
        }
        return p.data?.[field];
    };

    const [rowData, setRowData] = useState<Vendor[]>([]);
    const [isActiveToggle, setIsActiveToggle] = useState(true);
    const canEditVendors = !!editCreatePermission;
    const {pending, pendingCount, resetBaseline, makeEditable, setPending, getBaselineRows} = useGridEdit<Vendor>();
    const getRowId = useCallback((p: { data: Vendor }) => String(p.data.id), []);
    const gridContext = useMemo(() => ({pending}), [pending]);

    const gridOptions = useMemo(
        () => ({
            enableCellTextSelection: true,
            context: gridContext,
        }),
        [gridContext]
    );

    useEffect(() => {
        resetBaseline(vendors);
        originalActiveRef.current = new Map(
            (vendors ?? []).map(v => [String(v.id), !!v.is_active])
        );
        setRowData(vendors);
    }, [vendors, resetBaseline]);

    useEffect(() => {
        const api = gridRef.current;
        if (!api) return;
        const model = isActiveToggle
            ? { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } }
            : null;
        api.setFilterModel(model);
        api.onFilterChanged();
    }, [isActiveToggle, pendingCount]);

    useEffect(() => {
        gridRef.current?.refreshCells({force: true});
        gridRef.current?.refreshClientSideRowModel("filter");
    }, [pending]);

    const vendorTypeList = useMemo(
        () =>
            (vendorTypes ?? [])
                .map(t => ({
                    value: String(t.reference_value ?? "").trim(),
                    label: String(t.reference_label ?? "").trim(),
                }))
                .filter(t => t.value && t.label)
                .sort((a, b) => a.label.localeCompare(b.label)),
        [vendorTypes]
    );

    const vtValueToLabel = useMemo(
        () => new Map(vendorTypeList.map(t => [t.value, t.label] as const)),
        [vendorTypeList]
    );
    const vtLabelToValue = useMemo(
        () => new Map(vendorTypeList.map(t => [t.label, t.value] as const)),
        [vendorTypeList]
    );

    function generateColDefs(): ColDef<Vendor>[] {
        return [
            {
                headerName: "Id",
                valueGetter: (p) => p.data?.id,
                cellRenderer: createCellRenderer(
                    editCreatePermission,
                    (p) => `/vendors/${p.data.id}/edit`
                ),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Vendor Name",
                field: "name",
                ...makeEditable("name", {type: "text", canEdit: canEditVendors}, setRowData),
                filter: "agTextColumnFilter",
                pinned: "left",
            },
            {
                headerName: "Is Active?",
                field: "is_active",
                cellDataType: "text",
                sortable: true,
                filter: "agTextColumnFilter",
                ...makeEditable("is_active", {
                    type: "boolean",
                    canEdit: canEditVendors,
                    toggleOnClick: true
                }, setRowData),

                valueFormatter: (p) => (getPatchedValue(p, "is_active") ? "Yes" : "No"),

                filterValueGetter: (p: any) => {
                    const id = String(p.data?.id ?? "");
                    const patch = p.context?.pending?.[id] as { is_active?: boolean } | undefined;

                    if (patch && Object.prototype.hasOwnProperty.call(patch, "is_active")) {
                        return patch.is_active ? "Yes" : "No";
                    }

                    // Otherwise, use the baseline (original) value for filtering
                    const baseline = originalActiveRef.current.get(id);
                    return baseline ? "Yes" : "No";
                },
            },
            {
                headerName: "Vendor Type",
                field: "type",

                cellEditor: TextSuggestEditor,
                cellEditorPopup: true,
                cellEditorParams: (p: { value: any; data: Vendor }) => ({
                    values: vendorTypeList.map(t => t.label),
                    showAllOnEdit: true,
                    initialValue:
                        vtValueToLabel.get(String(p.value ?? p.data?.type ?? "").trim()) ??
                        String(p.value ?? p.data?.type ?? ""),
                }),

                ...makeEditable(
                    "type",
                    {
                        type: "text",
                        canEdit: canEditVendors,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const byLabel = vtLabelToValue.get(s);
                            return byLabel ?? s;
                        },
                        formatter: (v: any) =>
                            vtValueToLabel.get(String(v ?? "").trim()) ?? String(v ?? "").trim(),
                        equals: (a: any, b: any) =>
                            String(a ?? "").trim() === String(b ?? "").trim(),
                    },
                    setRowData
                ),

                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    vtValueToLabel.get(String((p.data as Vendor).type ?? "").trim()) ??
                    String((p.data as Vendor).type ?? "").trim(),
            },
            {
                headerName: "Point-of-Contact Name",
                field: "poc_name",
                ...makeEditable("poc_name", {type: "text", canEdit: canEditVendors}, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Point-of-Contact Email",
                field: "poc_email",
                ...makeEditable("poc_email", {type: "text", canEdit: canEditVendors}, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Point-of-Contact Phone Number",
                field: "poc_phone",
                ...makeEditable("poc_phone", {type: "text", canEdit: canEditVendors}, setRowData),
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Revenue Share",
                field: "revenue_share",
                ...makeEditable(
                    "revenue_share",
                    {
                        type: "text",
                        canEdit: canEditVendors,
                        parser: (nv: any, ov: any) => {
                            const s = String(nv ?? "").trim();
                            if (!s) return ov;
                            const num = Number(s.replace(/[^0-9.-]/g, ""));
                            if (Number.isNaN(num)) return ov;
                            let normalized = num >= 1 ? num / 100 : num;
                            if (normalized < 0) normalized = 0;
                            if (normalized > 1) normalized = 1;
                            return normalized;
                        },
                        formatter: (v: any) => {
                            const n = Number(v ?? 0);
                            return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : "";
                        },
                        equals: (a: any, b: any) => Number(a ?? 0) === Number(b ?? 0),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                filterValueGetter: (p) => {
                    const n = Number(getPatchedValue(p, "revenue_share") ?? 0);
                    return Number.isFinite(n) ? `${(n * 100).toFixed(2)}%` : "";
                },
                cellEditor: "agTextCellEditor",
                cellEditorParams: {useFormatter: true},
            },
            {
                headerName: "Sales Tax Responsible?",
                field: "sales_tax_responsible",
                cellDataType: "text",
                ...makeEditable(
                    "sales_tax_responsible",
                    {type: "boolean", canEdit: canEditVendors, toggleOnClick: true},
                    setRowData
                ),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => (p.value ? "Yes" : "No"),
                filterValueGetter: (p) => (getPatchedValue(p, "sales_tax_responsible") ? "Yes" : "No"),
            },
            {
                headerName: "Flat Fee",
                field: "flat_fee",
                cellDataType: "number",
                ...makeEditable(
                    "flat_fee",
                    {
                        type: "number",
                        canEdit: canEditVendors,
                        displayFactor: 100,
                        format: (v: number) => usdFormat.format((v ?? 0) / 100),
                    },
                    setRowData
                ),
                filter: "agTextColumnFilter",
                valueFormatter: (p) => (p.value ? usdFormat.format((p.value ?? 0) / 100) : "-"),
                filterValueGetter: (p) => {
                    const v = Number(getPatchedValue(p, "flat_fee") ?? 0);
                    return v ? usdFormat.format(v / 100) : "-";
                },
            },
            {
                headerName: "Updated By",
                valueGetter: (p) => p.data?.user?.username,
                filter: "agTextColumnFilter",
            },
            {
                headerName: "Created Date",
                valueGetter: (p) => p.data?.created_at,
                valueFormatter: (p) => {
                    const date = p.value ? moment(p.value) : null;
                    return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.created_at ? moment(p.data.created_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
            {
                headerName: "Updated Date",
                valueGetter: (p) => p.data?.updated_at,
                valueFormatter: (p) => {
                    const date = p.value ? moment(p.value) : null;
                    return date ? date.format("MM/DD/YYYY h:mm:ss a") : "";
                },
                filter: "agTextColumnFilter",
                filterValueGetter: (p) =>
                    p.data?.updated_at ? moment(p.data.updated_at).format("MM/DD/YYYY h:mm:ss a") : "",
            },
        ];
    }

    const colDefs: ColDef<Vendor>[] = useMemo(
        () => generateColDefs(),
        [makeEditable, setRowData, canEditVendors, editCreatePermission, vendorTypeList]
    );

    const defaultColDef = useMemo(() => ({
        valueGetter: (p: any) => p.colDef.field ? getPatchedValue(p, p.colDef.field) : p.value,
    }), []);

    const rowHeight = 50;
    const autoSizeStrategy: SizeColumnsToContentStrategy = {
        type: "fitCellContents",
    };
    const handleExportCSV = () => {
        if (gridRef.current) {
            const timestamp = new Date().toISOString().replace(/[-:]/g, "");
            const params = {
                fileName: `Vendors_${timestamp}.csv`,
                columnSeparator: ",",
            };
            gridRef.current.exportDataAsCsv(params);
        }
    };

    const mutateRow = async (id: number, patch: Partial<Vendor>) => {
        const user = getUserStore().user;
        const data = {
            ...patch,
            last_updated_by: user?.id,
            updated_at: new Date().toISOString()
        };
        const res = await client.mutate({
            mutation: UPDATE_VENDOR,
            variables: { id, data },
            errorPolicy: "all",
        });

        if (res.errors?.length) throw new Error(res.errors.map(e => e.message).join("; "));
        const updatedId = res.data?.update_vendors_by_pk?.id;
        if (!updatedId) throw new Error(`Update returned no id for vendor ${id}`);
    };

    const { handleSave } = useGridSave<Vendor>({
        rowData,
        setRowData,
        pending,
        setPending,
        resetBaseline,
        gridApiRef: gridRef as any,
        revalidate: () => revalidator.revalidate(),
        mutateRow,
        optimistic: true,
        baselines: [
            { ref: originalActiveRef, value: (r) => !!r.is_active },
        ],
    });

    return (
        <div className="w-full flex flex-col overflow-hidden" style={{ height: "calc(100vh - 128px)" }}>
            <div className="md:hidden grid grid-cols-6 min-w-0 px-0 py-0">
                <div className="col-span-6 flex items-center justify-between min-h-[48px] mb-1">
                    <div className="flex items-center gap-2 pl-2 pt-2">
                        <GridEditActions
                            pendingCount={pendingCount}
                            pending={pending}
                            gridRef={gridRef}
                            resetBaseline={resetBaseline}
                            baselineData={vendors}
                            isActiveToggle={isActiveToggle}
                            setRowData={setRowData}
                            setPending={setPending}
                            onSave={handleSave}
                            getBaselineRows={getBaselineRows}
                        />
                    </div>

                    <div className="ml-2 flex flex-col items-center">
                        <span className="block text-xs font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                        <Switch
                            checked={isActiveToggle}
                            onChange={() => setIsActiveToggle((prev) => !prev)}
                            onColor="#EE2E30"
                            onHandleColor="#FFFFFF"
                            handleDiameter={18}
                            uncheckedIcon={false}
                            checkedIcon={false}
                            boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                            activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                            height={15}
                            width={48}
                        />
                    </div>
                </div>

                <div className="col-span-6 flex items-center gap-2">
                    <div className="flex-1 min-w-0">
                        <div className="w-full px-3 py-2">
                            <Input
                                name="search"
                                placeholder="Search Vendors"
                                onChange={(s) => {
                                    navigate(s && typeof s === "string" && s.length > 0 ? `/vendors/list?search=${s}` : `/vendors/list`);
                                    revalidator.revalidate();
                                }}
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-1 shrink-0">
                        <SecondaryButton onClick={handleExportCSV}>Export to CSV</SecondaryButton>
                        {editCreatePermission && (
                            <PrimaryButton onClick={() => navigate(`/vendors/create`)}>Create</PrimaryButton>
                        )}
                    </div>
                </div>
            </div>

            <div className="hidden md:grid grid-cols-12 items-center gap-3 min-w-0 py-2">
                <div className="col-span-5 min-w-0">
                    <div className="w-full">
                        <Input
                            name="search"
                            placeholder="Search Vendors"
                            onChange={(s) => {
                                navigate(s && typeof s === "string" && s.length > 0 ? `/vendors/list?search=${s}` : `/vendors/list`);
                                revalidator.revalidate();
                            }}
                        />
                    </div>
                </div>

                <div className="col-span-3 col-start-6 justify-self-start flex items-center gap-1">
                    <GridEditActions
                        pendingCount={pendingCount}
                        pending={pending}
                        gridRef={gridRef}
                        resetBaseline={resetBaseline}
                        baselineData={vendors}
                        isActiveToggle={isActiveToggle}
                        setRowData={setRowData}
                        setPending={setPending}
                        onSave={handleSave}
                        getBaselineRows={getBaselineRows}
                    />
                </div>

                <div className="col-span-1 flex flex-col items-center">
                    <span className="block text-sm font-medium text-gray-500 whitespace-nowrap">Active Only</span>
                    <Switch
                        checked={isActiveToggle}
                        onChange={() => setIsActiveToggle((prev) => !prev)}
                        onColor="#EE2E30"
                        onHandleColor="#FFFFFF"
                        handleDiameter={18}
                        uncheckedIcon={false}
                        checkedIcon={false}
                        boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                        activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                        height={15}
                        width={48}
                    />
                </div>

                <div className="col-span-3 flex justify-end items-center gap-1 whitespace-nowrap">
                    <SecondaryButton onClick={handleExportCSV}>
                        <span className="whitespace-nowrap">Export&nbsp;to&nbsp;CSV</span>
                    </SecondaryButton>
                    {editCreatePermission && (
                        <PrimaryButton onClick={() => navigate(`/vendors/create`)}>Create</PrimaryButton>
                    )}
                </div>
            </div>

            <div className="flex-1 min-h-0">
                <div className="ag-theme-quartz w-full h-full min-w-0">
                    <AgGridReact
                        rowData={rowData}
                        columnDefs={colDefs}
                        defaultColDef={defaultColDef}
                        rowHeight={rowHeight}
                        autoSizeStrategy={autoSizeStrategy}
                        pagination
                        gridOptions={gridOptions}
                        paginationPageSize={100}
                        getRowId={getRowId}
                        onGridReady={(params) => {
                            gridRef.current = params.api;
                            if (isActiveToggle) {
                                const model = { is_active: { filterType: 'text', type: 'equals', filter: 'Yes' } };
                                params.api.setFilterModel(model);
                                params.api.onFilterChanged();
                            }
                        }}
                        singleClickEdit
                        stopEditingWhenCellsLoseFocus
                    />
                </div>
            </div>
        </div>
    );
};
